import { TextButton } from "@/components/ui/pressable";
import type { ComponentProps, ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

export type TaskCardColumn = {
  key: string;
  label?: ReactNode;
  className?: string;
};

/**
 * Clean task table: uppercase column labels and each row as its own card.
 * Shared by every task list (Calendar list, Project tasks, Account tasks).
 */
export const TaskCardTable = ({
  columns,
  children,
  className,
}: {
  columns: TaskCardColumn[];
  children: ReactNode;
  className?: string;
}) => (
  <div className={cn("overflow-x-auto", className)}>
    <table className="-my-1.5 w-full min-w-[40rem] border-separate border-spacing-y-1.5 text-sm">
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={cn(
                "px-3 pb-1 text-left text-xs font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase",
                column.className,
              )}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const TaskCardRow = ({
  children,
  onClick,
  muted = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  muted?: boolean;
}) => (
  <tr
    onClick={onClick}
    className={cn(
      "group/task-row [&>td]:border-y [&>td]:bg-card [&>td]:px-3 [&>td]:py-3 [&>td]:align-middle [&>td]:transition-colors [&>td:first-child]:rounded-l-lg [&>td:first-child]:border-l [&>td:last-child]:rounded-r-lg [&>td:last-child]:border-r",
      onClick && "cursor-pointer hover:[&>td]:bg-muted/40",
      muted && "[&>td]:text-muted-foreground",
    )}
  >
    {children}
  </tr>
);

/** Table cell that does not trigger the row click (checkbox, links, menus). */
export const TaskCardActionCell = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => (
  <td className={className} onClick={(event) => event.stopPropagation()}>
    {children}
  </td>
);

export const TaskRoundCheckbox = ({
  className,
  ...props
}: ComponentProps<typeof Checkbox>) => (
  <Checkbox
    {...props}
    className={cn(
      "size-5 rounded-full border-muted-foreground/40 data-[state=checked]:border-success data-[state=checked]:bg-success",
      className,
    )}
  />
);

export const TaskSortHeader = ({
  label,
  direction,
  onToggle,
}: {
  label: string;
  direction: "asc" | "desc";
  onToggle: () => void;
}) => (
  <TextButton
    tone="muted"
    size="inherit"
    onClick={onToggle}
    className="uppercase"
    aria-label={`Sort by ${label.toLowerCase()} ${direction === "asc" ? "descending" : "ascending"}`}
  >
    {label}
    {direction === "asc" ? (
      <ArrowUp className="size-3" />
    ) : (
      <ArrowDown className="size-3" />
    )}
  </TextButton>
);
